import { useState, useEffect, useCallback } from 'react';
import { writeFetch, getSession } from '../api';

const DEFAULT_PREFS = {
  notify_on_candle: true,
  notify_on_flower: true,
  notify_on_message: true,
  notify_inapp: true,
  notify_email: true,
};

// Fetch & manage notification preferences for a memorial + current user
export function useTributeNotifications(memorialPageId) {
  const [prefs, setPrefs] = useState(null);
  const [prefId, setPrefId] = useState(null);
  const [loading, setLoading] = useState(true);

  const user = getSession();

  const fetchPrefs = useCallback(async () => {
    if (!user || !memorialPageId) { setLoading(false); return; }
    try {
      const res = await writeFetch(
        `read/memorial_notification_preferences?user_id=${user.id}&memorial_page_id=${memorialPageId}`
      );
      const data = await res.json();
      if (data.status === 'success' && data.data?.length > 0) {
        const p = data.data[0];
        setPrefId(p.id);
        setPrefs({
          notify_on_candle:  p.notify_on_candle  === 1 || p.notify_on_candle  === true,
          notify_on_flower:  p.notify_on_flower   === 1 || p.notify_on_flower   === true,
          notify_on_message: p.notify_on_message  === 1 || p.notify_on_message  === true,
          notify_inapp:      p.notify_inapp !== 0 && p.notify_inapp !== false, // default true
          notify_email:      p.notify_email  === 1 || p.notify_email  === true,
        });
      } else {
        setPrefs({ ...DEFAULT_PREFS });
        setPrefId(null);
      }
    } catch {
      setPrefs({ ...DEFAULT_PREFS });
    } finally {
      setLoading(false);
    }
  }, [memorialPageId, user?.id]);

  useEffect(() => { fetchPrefs(); }, [fetchPrefs]);

  const savePrefs = useCallback(async (newPrefs) => {
    if (!user || !memorialPageId) return;
    const payload = {
      user_id:          user.id,
      memorial_page_id: memorialPageId,
      notify_on_candle:  newPrefs.notify_on_candle  ? 1 : 0,
      notify_on_flower:  newPrefs.notify_on_flower   ? 1 : 0,
      notify_on_message: newPrefs.notify_on_message  ? 1 : 0,
      notify_inapp:      newPrefs.notify_inapp !== false ? 1 : 0,
      notify_email:      newPrefs.notify_email   ? 1 : 0,
      updated_at:        new Date().toISOString(),
    };

    if (prefId) {
      await writeFetch(`update/memorial_notification_preferences/${prefId}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
    } else {
      const res = await writeFetch('create/memorial_notification_preferences', {
        method: 'POST',
        body: JSON.stringify({ ...payload, created_at: new Date().toISOString() }),
      });
      const data = await res.json();
      if (data?.id) setPrefId(data.id);
    }
    setPrefs(newPrefs);
  }, [prefId, user?.id, memorialPageId]);

  return { prefs, loading, savePrefs };
}

// Notify all users connected to a memorial about a tribute activity
export async function notifyMemorialFollowers({ memorialPageId, memorialName, actorName, tributeType, giftType }) {
  try {
    const res = await writeFetch(
      `read/memorial_notification_preferences?memorial_page_id=${memorialPageId}`
    );
    const data = await res.json();
    if (data.status !== 'success' || !data.data?.length) return;

    const typeMap = {
      candle:  { field: 'notify_on_candle',  emoji: '🕯️', label: 'lit a candle' },
      flower:  { field: 'notify_on_flower',  emoji: '🌹', label: 'left flowers' },
      dove:    { field: 'notify_on_message', emoji: '🕊️', label: 'sent a dove' },
      prayer:  { field: 'notify_on_message', emoji: '🙏', label: 'offered a prayer' },
      tribute: { field: 'notify_on_message', emoji: '💬', label: 'signed the guestbook' },
    };

    const config = typeMap[tributeType] || typeMap.tribute;
    const optLabel = giftType ? ` (${giftType})` : '';
    const title = `New tribute for ${memorialName}`;
    const message = `${actorName} ${config.label}${optLabel} at ${memorialName}'s memorial.`;

    const notifyPromises = data.data
      .filter(p => {
        const eventOn = p[config.field] === 1 || p[config.field] === true;
        const inAppOn  = p.notify_inapp !== 0 && p.notify_inapp !== false;
        return eventOn && inAppOn;
      })
      .map(p =>
        writeFetch('create/notifications_1741164676', {
          method: 'POST',
          body: JSON.stringify({
            id:         `notif-tribute-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`,
            user_id:    p.user_id,
            title,
            message:    `${config.emoji} ${message}`,
            type:       'tribute',
            is_read:    0,
            owner_id:   p.user_id,
            created_at: new Date().toISOString(),
          }),
        })
      );

    await Promise.allSettled(notifyPromises);
  } catch (e) {
    console.error('Notification dispatch failed', e);
  }
}